import { Alert, AppBar, Backdrop, Box, Button, CircularProgress, Container, Snackbar, Toolbar, Typography } from "@mui/material";
import { selectUser } from "../../../redux/user/userSlice";
import { useSelector } from "react-redux";
import FragmentFullContent from "../../../features/base/FragmentFullContent";
import { useAppDispatch } from "../../../app/hooks";
import { classroomSlice, selectClassroom } from "../../../redux/classroom/classroomSlice";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { schoolSlice, selectSchool } from "../../../redux/school/schoolSlice";
import { selectClassroomStudents, classroomStudentsSlice, retrieveClassroomStudents } from "../../../redux/classroom/classroomStudentsSlice";
import { ContextMenu, ContextMenuOptions } from "../../../features/base/ContextMenu";
import ClassroomPageTitle from "../basics/ClassroomPageTitle";
import ClassroomPageAvatar from "../basics/ClassroomPageAvatar";
import PageAuthenticatedLoading from "../../../features/pages/PageAuthenticatedLoading";
import { LoadState } from "../../../redux/common";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { schoolStudentsSlice } from "../../../redux/school/schoolStudentsSlice";
import PossibleStudentsList from "./PossibleStudentsList";
import { classroomStudentAddSlice, selectClassroomStudentAdd } from "../../../redux/classroom/classroomStudentAddSlice";
import ExistingStudentsList from "./ExistingStudentsList";
import { studentClassroomsSlice } from "../../../redux/student/studentClassroomsSlice";
import { classroomRemoveStudentSlice, selectClassroomRemoveStudent } from "../../../redux/classroom/classroomRemoveStudentSlice";
import { routeSchool } from "../../../routes/school";
import { routeClassroom } from "../../../routes/classroom";
import { TopBar } from "../../../features/top/TopBar";

export function ClassroomStudentAdd() {
    const user = useSelector(selectUser);
    const school = useSelector(selectSchool);
    const classroom = useSelector(selectClassroom);
    const classroomStudents = useSelector(selectClassroomStudents);
    const addStudent = useSelector(selectClassroomStudentAdd);
    const removeStudent = useSelector(selectClassroomRemoveStudent);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { schoolId, classroomId } = useParams();
    // console.log(`SchoolId: ${schoolId}/${school.state}, ClassroomId: ${classroomId}/${classroom.state}, ClassroomStudents: ${classroomStudents.state}, SchoolStudents: ${schoolStudents.state}, AddStudent: ${addStudent.state}, RemoveStudent: ${removeStudent.state}`);
    if ((!schoolId) || (!classroomId)) {
        return <FragmentFullContent>
            <Typography variant="h4" component="div">
                Parâmetros inválidos
            </Typography>
            <Button onClick={() => { navigate(-1); }}>Voltar</Button>
        </FragmentFullContent>;
    }

    if (classroomStudents.state.state == LoadState.NotStarted && isTokenReady(user)) {
        dispatch(retrieveClassroomStudents({ schoolId: schoolId, classroomId: classroomId }));
    }

    const cleanup = () => {
        console.debug("Cleaning up");
        dispatch(schoolSlice.actions.clean());
        dispatch(classroomSlice.actions.clean());
        dispatch(classroomStudentsSlice.actions.clean());
        dispatch(schoolStudentsSlice.actions.clean());
        dispatch(classroomStudentAddSlice.actions.clean());
    };

    if (addStudent.state.state == LoadState.Loaded) {
        if (addStudent.student) {
            dispatch(classroomStudentsSlice.actions.add(addStudent.student));
            dispatch(schoolStudentsSlice.actions.remove(addStudent.student));
            dispatch(classroomStudentAddSlice.actions.clean());
            dispatch(studentClassroomsSlice.actions.clean());
        } else {
            cleanup();
        }
    }
    if (removeStudent.state.state == LoadState.Loaded) {
        if (removeStudent.student) {
            dispatch(classroomStudentsSlice.actions.remove(removeStudent.student));
            dispatch(schoolStudentsSlice.actions.add(removeStudent.student));
            dispatch(classroomRemoveStudentSlice.actions.clean());
            dispatch(studentClassroomsSlice.actions.clean());
        } else {
            cleanup();
        }
    }

    const dismissAddError = () => {
        dispatch(classroomStudentAddSlice.actions.clean());
    };
    const dismissRemoveError = () => {
        dispatch(classroomRemoveStudentSlice.actions.clean());
    };

    // build the context menu
    const options: ContextMenuOptions[] = [];
    options.push({ label: "Atualizar", click: cleanup });

    // build the breadcrumbs for school and classroom
    const breadcrumbs = [
        { label: school.selected?.name || "Escola", href: routeSchool(schoolId, "classroom") },
        { label: classroom.selected?.name || "Sala de aula", },
    ];

    return <PageAuthenticatedLoading
        topbar={
            <TopBar
                topMenu={<ContextMenu options={options} />}
                breadcrumbs={breadcrumbs}
                title={<ClassroomPageTitle schoolId={schoolId} classroomId={classroomId} />}
                pageAvatar={<ClassroomPageAvatar />}
            />
        }
        loading={{
            states: [school.state, classroom.state],
            content: () => {
                return <>
                    <AppBar position="static" color="default">
                        <Toolbar>
                            <Typography variant="h6" component="div">
                                Adicionar estudante
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <Button onClick={() => navigate(routeClassroom(schoolId, classroomId, "main"))}>Voltar</Button>
                        </Toolbar>
                    </AppBar>
                    <ExistingStudentsList schoolId={schoolId} classroomId={classroomId} />
                    <PossibleStudentsList schoolId={schoolId} classroomId={classroomId} />
                    <Container sx={{ mb: 4 }}>
                    </Container>
                    <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                        open={addStudent.state.state == LoadState.Loading || removeStudent.state.state == LoadState.Loading} >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                    <Snackbar open={addStudent.state.state == LoadState.Failed} autoHideDuration={5000} onClose={dismissAddError}>
                        <Alert onClose={dismissAddError} severity="error" variant="filled" sx={{ width: "100%" }}> Erro ao adicionar estudante. </Alert>
                    </Snackbar>
                    <Snackbar open={removeStudent.state.state == LoadState.Failed} autoHideDuration={5000} onClose={dismissRemoveError}>
                        <Alert onClose={dismissRemoveError} severity="error" variant="filled" sx={{ width: "100%" }}> Erro ao remover estudante. </Alert>
                    </Snackbar>
                </>;
            },
            error: "Erro ao carregar informações",
            retry: cleanup
        }} />;
}
